import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import * as yup from 'yup'
import { gtmEvent } from 'lib/gtm'
import { yupResolver } from '@hookform/resolvers'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { useSnackbar } from 'notistack'
import Box from '@material-ui/core/Box'
import { genderOptions } from 'lib/form-schemas/common'
import Layout from './layout'
import RegistrationSubmitting from '../registration-submitting'
import {
  REGISTER_PGA_HOPE_GROUP_EVENT,
  CURRENT_USER_INFO,
  PGA_HOPE_PARTICIPATION,
} from './queries'
import OneTimeOnlyDialog from './one-time-only-dialog'
import VerifyAccessCode from 'components/verify-access-code'
import * as Common from 'lib/form-schemas/common'
import { branchOfServiceOptions } from './constants'
import { handednessOptions } from 'utils/constants'
import { formatISO } from 'date-fns'
import RegistrationComplete from './registration-complete'
import {
  emailsMatch,
  INVITE_EMAIL_MISMATCH_MESSAGE,
  useRegistrationInvite,
} from '../registration-invite'
import RegistrationFullDialog from '../components/registration-full-dialog'
import { isRegistrationFullError } from '../utils/registration-full'
import { useAuth } from 'lib/auth'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { stripPhoneNumber } from 'utils/phoneNumber'
import LinearProgress from '@material-ui/core/LinearProgress'
import { DateTime } from 'luxon'

const phoneRegExp = /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/
const zipCodeRegExp = /^\d{5}$/

const formatPhoneNumber = phoneNumber => {
  const formatted = phoneNumber.replace(/\D/g, '')
  return formatted.length > 0 ? formatted : null
}

const formatDob = dob => {
  return formatISO(dob).slice(0, 10) // save only the date portion of the ISO string
}

const emergencyContactSchema = {
  firstName: yup
    .string()
    .label('First Name')
    .required(),
  lastName: yup
    .string()
    .label('Last Name')
    .required(),
  phone: yup
    .string()
    .matches(phoneRegExp, 'Your phone number must be 10 digits')
    .label('Phone Number')
    .required(),
  relationship: yup
    .string()
    .label('Relationship')
    .required(),
}

export const dateOfBirthSchema = yup
  .date()
  .nullable()
  .typeError('Enter a real date of birth')
  .required()
  .min(new Date(1900, 0, 1), 'Year must be 1900 or later')
  .test(
    'not-future',
    "Date of Birth can't be in the future",
    value => !value || value <= new Date(),
  )
  .label('Date of Birth')

const studentSchema = {
  firstName: yup
    .string()
    .label('First Name')
    .required(),
  lastName: yup
    .string()
    .label('Last Name')
    .required(),
  phone: yup
    .string()
    .matches(phoneRegExp, 'Your phone number must be 10 digits')
    .label('Phone Number')
    .required(),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('This field is required'),
  dateOfBirth: dateOfBirthSchema,
  gender: yup
    .string()
    .label('Gender')
    .required()
    .oneOf(
      genderOptions.map(Common.getValue),
      'Gender must be one of the available options',
    ),
  zipCode: yup
    .string()
    .matches(zipCodeRegExp, 'Must be a valid zip code')
    .required(),
}

const pgaHopeProfileSchema = {
  accommodations: yup.string(),
  additionalInfo: yup.string(),
  golfCareerInterest: yup.boolean().required(),
  golfExperience: yup.string().required(),
  handedness: yup
    .string()
    .oneOf(
      handednessOptions.map(Common.getValue),
      'Handedness must be one of the available options',
    )
    .required(),
  hasOwnClubs: yup.boolean().required(),
  serviceBranch: yup
    .string()
    .oneOf(
      branchOfServiceOptions.map(Common.getValue),
      'Branch of service must be one of the available options',
    )
    .required(),
  referralSource: yup.string().required(),
  militaryStatus: yup
    .string()
    .required()
    .label('current status'),
  serviceYearStart: yup
    .number()
    .transform((_, val) => (val ? Number(val) : null))
    .label('From')
    .required(),
  serviceYearEnd: yup
    .number()
    .nullable()
    .transform((_, val) => {
      if (val === 'Present') {
        return null
      }
      return val ? Number(val) : null
    })
    .label('To'),
}

const validationSchema = ({ inviteEmail, waiverEnabled = false } = {}) =>
  yup.object().shape({
    student: yup.object().shape({
      ...studentSchema,
      ...(inviteEmail && {
        email: studentSchema.email.test(
          'matches-invite-email',
          INVITE_EMAIL_MISMATCH_MESSAGE,
          value => emailsMatch(value, inviteEmail),
        ),
      }),
    }),
    emergencyContact: yup.object().shape(emergencyContactSchema),
    pgaHopeProfile: yup.object().shape(pgaHopeProfileSchema),
    ...(waiverEnabled && {
      agreeWaiver: yup
        .boolean()
        .oneOf([true], 'You must agree to the waiver to continue')
        .required('You must agree to the waiver to continue'),
    }),
  })

const PgaHopeRegistrationForm = ({ groupEvent, isMobile, fromWaitlist }) => {
  const [registrationEmail, setRegistrationEmail] = useState(null)
  const [isRegistrationFullOpen, setIsRegistrationFullOpen] = useState(false)
  const [isOneTimeOnlyOpen, setIsOneTimeOnlyOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [isAccessCodeVerified, setIsAccessCodeVerified] = useState(false)
  const requiresAccessCode = groupEvent.requiresAccessCode
  const [waiverEnabled, participationCheckEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_PGA_HOPE_WAIVER,
    flags.FLAG_FEAT_PGA_HOPE_PARTICIPATION_CHECK,
  ])

  const { user, isConsumer } = useAuth()
  const isAuthenticatedConsumer = user && isConsumer
  const {
    isValid: inviteIsValid,
    token: inviteToken,
    firstName: inviteFirstName,
    lastName: inviteLastName,
    email: inviteEmail,
  } = useRegistrationInvite()

  const { data, loading } = useQuery(CURRENT_USER_INFO, {
    skip: !isAuthenticatedConsumer,
    onCompleted: () => {
      prefillStudentValues()
    },
  })

  const loggedInUser = data?.currentUser?.attributes || {}
  const currentStudent =
    loggedInUser?.representatives?.filter(
      representative => representative.relationshipType === 'SELF',
    )[0]?.student || {}

  const prefillStudentValues = () => {
    if (currentStudent) {
      form.setValue('student', {
        zipCode: currentStudent?.pgaHopeProfile?.zipCode,
        gender: currentStudent?.golferProfile?.gender,
      })
      form.setValue('pgaHopeProfile', {
        golfCareerInterest:
          currentStudent?.pgaHopeProfile?.golfCareerInterest ?? false,
        golfExperience: currentStudent?.pgaHopeProfile?.golfExperience,
        handedness: currentStudent?.pgaHopeProfile?.handedness,
        hasOwnClubs: currentStudent?.pgaHopeProfile?.hasOwnClubs,
        militaryStatus: currentStudent?.pgaHopeProfile?.militaryStatus,
        referralSource: currentStudent?.pgaHopeProfile?.referralSource,
        serviceBranch: currentStudent?.pgaHopeProfile?.serviceBranch,
        serviceYearStart: currentStudent?.pgaHopeProfile?.serviceYearStart,
        serviceYearEnd: currentStudent?.pgaHopeProfile?.serviceYearEnd,
        accommodations: currentStudent?.pgaHopeProfile?.accommodations,
        additionalInfo: currentStudent?.pgaHopeProfile?.additionalInfo,
      })
    }
  }

  const { enqueueSnackbar } = useSnackbar()
  const form = useForm({
    defaultValues: {
      student: {
        firstName: isAuthenticatedConsumer
          ? user.firstName
          : inviteIsValid
            ? inviteFirstName
            : '',
        lastName: isAuthenticatedConsumer
          ? user.lastName
          : inviteIsValid
            ? inviteLastName
            : '',
        email: isAuthenticatedConsumer
          ? user.email
          : inviteIsValid
            ? inviteEmail
            : '',
        phone: isAuthenticatedConsumer
          ? stripPhoneNumber(user.phoneNumber)
          : '',
        gender: currentStudent?.golferProfile?.gender || '',
      },
      pgaHopeProfile: {
        golfCareerInterest:
          currentStudent?.pgaHopeProfile?.golfCareerInterest || false,
        golfExperience: currentStudent?.pgaHopeProfile?.golfExperience || '',
        handedness: currentStudent?.pgaHopeProfile?.handedness || '',
        hasOwnClubs: currentStudent?.pgaHopeProfile?.hasOwnClubs || null,
        militaryStatus: currentStudent?.pgaHopeProfile?.militaryStatus || '',
        referralSource: currentStudent?.pgaHopeProfile?.referralSource || '',
        serviceBranch: currentStudent?.pgaHopeProfile?.serviceBranch || '',
        serviceYearStart:
          currentStudent?.pgaHopeProfile?.serviceYearStart || '',
        serviceYearEnd:
          currentStudent?.pgaHopeProfile?.serviceYearEnd || 'Present',
        accommodations: currentStudent?.pgaHopeProfile?.accommodations || '',
        additionalInfo: currentStudent?.pgaHopeProfile?.additionalInfo || '',
        zipCode: currentStudent?.pgaHopeProfile?.zipCode || '',
      },
      agreeWaiver: false,
    },
    resolver: yupResolver(
      validationSchema({
        inviteEmail: inviteIsValid ? inviteEmail : null,
        waiverEnabled,
      }),
    ),
  })
  const [registerPgaHopeGroupEvent] = useMutation(REGISTER_PGA_HOPE_GROUP_EVENT)
  const apolloClient = useApolloClient()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [activeStep])

  // PGA HOPE is one-time only. A failed lookup must not block a legitimate
  // registration, so errors are reported and the golfer continues.
  const verifyFirstTimeParticipant = async ({
    email,
    dateOfBirth,
    recaptchaToken,
  }) => {
    try {
      // The shared client defaults to errorPolicy 'all', which resolves GraphQL
      // errors instead of throwing. 'none' makes them reject into the catch below.
      const { data, errors } = await apolloClient.query({
        query: PGA_HOPE_PARTICIPATION,
        variables: { email: email.trim(), dateOfBirth: formatDob(dateOfBirth) },
        fetchPolicy: 'network-only',
        errorPolicy: 'none',
        context: { recaptchaToken },
      })
      // Never treat a GraphQL error as a clean check, even if a resolved
      // result carries one.
      if (errors?.length) {
        throw new Error(errors[0].message)
      }
      if (data?.pgaHopeParticipation?.previouslyParticipated) {
        setIsOneTimeOnlyOpen(true)
        return false
      }
    } catch (error) {
      window.rg4js &&
        window.rg4js('send', {
          error,
          tags: ['pga-hope-group-event', 'participation-check'],
        })
    }
    return true
  }

  const onSubmit = async values => {
    return registerPgaHopeGroupEvent({
      variables: {
        id: groupEvent.id,
        params: {
          student: {
            firstName: values.student.firstName,
            lastName: values.student.lastName,
            email: values.student.email,
            phone: formatPhoneNumber(values.student.phone),
            dateOfBirth: formatDob(values.student.dateOfBirth),
            gender: values.student.gender,
          },
          emergencyContact: {
            firstName: values.emergencyContact.firstName,
            lastName: values.emergencyContact.lastName,
            relationship: values.emergencyContact.relationship,
            phone: formatPhoneNumber(values.emergencyContact.phone),
          },
          pgaHopeProfile: {
            golfCareerInterest: values.pgaHopeProfile.golfCareerInterest,
            golfExperience: values.pgaHopeProfile.golfExperience,
            handedness: values.pgaHopeProfile.handedness,
            hasOwnClubs: values.pgaHopeProfile.hasOwnClubs,
            militaryStatus: values.pgaHopeProfile.militaryStatus,
            referralSource: values.pgaHopeProfile.referralSource,
            serviceBranch: values.pgaHopeProfile.serviceBranch,
            serviceYearStart: values.pgaHopeProfile.serviceYearStart,
            serviceYearEnd: values.pgaHopeProfile.serviceYearEnd,
            accommodations: values.pgaHopeProfile.accommodations,
            additionalInfo: values.pgaHopeProfile.additionalInfo,
            zipCode: values.student.zipCode,
          },
          invitationToken: inviteToken,
        },
      },
    })
      .then(({ data }) => {
        if (data.registerPgaHopeGroupEvent.success) {
          setRegistrationEmail(values?.student?.email)
          gtmEvent({
            event: 'complete-event-registration',
            title: groupEvent.title,
            category: groupEvent.tags,
            event_date: DateTime.fromISO(groupEvent.startDateTime, {
              zone: groupEvent.coachFacility.timezone,
            }).toFormat('yyyy-LL-dd'),
            event_time: DateTime.fromISO(groupEvent.startDateTime, {
              zone: groupEvent.coachFacility.timezone,
            }).toFormat('hh:mm a'),
            facility: groupEvent.coachFacility.name,
            price: groupEvent.priceInCents / 100,
            coach_id: groupEvent.coach.id,
            coach_name: groupEvent.coach.name,
            has_plus_subscription: groupEvent.coach.hasPaidTierSubscription,
          })
          // Remove the below event in the future.
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'register-group-event',
            formAction: 'completed-registration',
            formStatus: 'pga-hope-event',
          })
          setActiveStep(waiverEnabled ? 3 : 2)
        } else if (
          isRegistrationFullError(data.registerPgaHopeGroupEvent.message)
        ) {
          setIsRegistrationFullOpen(true)
        } else {
          enqueueSnackbar(data.registerPgaHopeGroupEvent.message, {
            variant: 'error',
          })
        }
      })
      .catch(error => {
        if (isRegistrationFullError(error)) {
          setIsRegistrationFullOpen(true)
          return
        }
        window.rg4js &&
          window.rg4js('send', {
            error: error,
            tags: ['pga-hope-group-event', 'registration'],
          })
        enqueueSnackbar('An error has occurred. Please try again later.', {
          variant: 'error',
        })
      })
  }
  if (loading) return <LinearProgress color="secondary" />

  return (
    <>
      {form.formState.isSubmitting && !form.formState.isValidating && (
        <RegistrationSubmitting />
      )}
      <Box
        display={
          !form.formState.isSubmitting || form.formState.isValidating
            ? 'block'
            : 'none'
        }
      >
        {registrationEmail ? (
          <RegistrationComplete
            groupEvent={groupEvent}
            registrationEmail={registrationEmail}
            isMobile={isMobile}
            waiverEnabled={waiverEnabled}
          />
        ) : (
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Layout
                groupEvent={groupEvent}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                isMobile={isMobile}
                onSubmit={onSubmit}
                waiverEnabled={waiverEnabled}
                verifyFirstTimeParticipant={
                  participationCheckEnabled
                    ? verifyFirstTimeParticipant
                    : undefined
                }
              />
            </form>
          </FormProvider>
        )}
      </Box>
      <VerifyAccessCode
        groupEvent={groupEvent}
        isOpen={requiresAccessCode && !isAccessCodeVerified}
        setIsOpen={setIsAccessCodeVerified}
        coachName={groupEvent?.coach?.name}
        setVerifiedAccessCode={setIsAccessCodeVerified}
      />
      <RegistrationFullDialog
        isOpen={isRegistrationFullOpen}
        onClose={() => setIsRegistrationFullOpen(false)}
        groupEvent={groupEvent}
        fromWaitlist={fromWaitlist}
      />
      <OneTimeOnlyDialog
        isOpen={isOneTimeOnlyOpen}
        onClose={() => setIsOneTimeOnlyOpen(false)}
      />
    </>
  )
}

export { validationSchema }
export default PgaHopeRegistrationForm

PgaHopeRegistrationForm.propTypes = {
  groupEvent: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  fromWaitlist: PropTypes.bool,
}
